import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Moment } from 'moment';
import { Observable } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { AuthService } from '../core/authentication/auth.service';
import { AppConfig } from '../app-config';
import { APP_CONFIG } from '../app-config.token';
import { ApproveAgreementVoidingWithTokenRequest } from '../contracts/requests/approve-agreement-voiding-with-token-request';
import { CreateMultipleWorkerAgreementsRequest } from '../contracts/requests/create-multiple-worker-agreements-request';
import { GenerateAgreementApprovalCodeRequest } from '../contracts/requests/GenerateAgreementApprovalCodeRequest';
import { SendAgreementExtensionConsentRequest } from '../contracts/requests/send-agreement-extension-consent-request';
import { SendMultipleWorkerAgreementsToApprovalRequest } from '../contracts/requests/send-multiple-worker-agreements-to-approval-request';
import { SignWorkerAgreementRequest } from '../contracts/requests/sign-worker-agreement-request';
import { TerminateAgreementRequest } from '../contracts/requests/terminate-agreement-request';
import { UndoVoidingAgreementRequest } from '../contracts/requests/undo-voiding-agreement-request';
import { VerifyAgreementApprovalCodeRequest } from '../contracts/requests/VerifyAgreementApprovalCodeRequest';
import { VoidAgreementRequest } from '../contracts/requests/void-agreement-request';
import { AddEmployeeAgreementResponse } from '../contracts/responses/add-employee-agreement-response';
import { Filter } from '../models/common/filter';
import { DictionaryItem } from '../models/dictionary-item';
import { AbsenceSubtypeDto } from '../models/dtos/absence-subtype-dto';
import { AgreementToPick } from '../models/dtos/agreement-to-pick-dto';
import { AllWorkerAgreementGridDto } from '../models/dtos/AllWorkerAgreementGridDto';
import { HolidayCalendarDto } from '../models/dtos/holiday-dto';
import { NoticePeriodDto } from '../models/dtos/notice-period-dto';
import { WorkerAgreementDetailsDto } from '../models/dtos/worker-agreement-details-dto';
import { WorkerAgreementGridDto } from '../models/dtos/worker-agreement-grid-dto';
import { WorkerPIT2AgreementsCompaniesDto } from '../models/dtos/WorkerPIT2AgreementCompaniesDto';
import { AgreementTypeEnum } from '../models/enums/worker-agreement-type-enum';
import { WorkerAgreement } from '../models/WorkerAgreement';
import { IPagedResult } from '../shared/models/PagedResult';
import { MultipleWorkerAgreementsActionConfigDto } from '../models/dtos/multiple-worker-agreements-action-config-dto';
import { MultipleWorkerAgreementsActionSelectedWorkersDto } from '../models/dtos/multiple-worker-agreements-action-selected-workers-dto';
import { MultipleWorkerAgreementsActionSelectedWorkerAgreementDto } from '../models/dtos/multiple-worker-agreements-action-selected-worker-agreement-dto';
import { MandateAgreementWorkedDayGridDto } from '../models/dtos/mandate-agreement-worked-day-grid-dto';
import { MandateAgreementWorkedDaysWorkerGridDto } from '../models/dtos/mandate-agreement-worked-days-grid-dto';
import { AgreementToLogWork } from '../models/dtos/agreement-to-log-work';
import { BYPASS_ERROR_HANDLER } from '../common/utils/http-context-tokens';
import { EmploymentType } from '../models/enums/employment-type-enum';
import { LegalizationDataForWorkerAgreementDto } from '../models/dtos/worker-agreements/legalization-data-for-worker-agreement-dto';
import { WorkerAgreementForAnnexDto } from '../models/dtos/worker-agreements/worker-agreement-for-annex-dto';
import { ActiveAgreementResultDto as ActiveAgreementDto } from '../models/dtos/active-agreement-dto';
import { WorkerAgreementDetailsResponse } from '../models/dtos/annexes/annex-list-with-details-dto';

@Injectable({
  providedIn: 'root',
})
export class WorkerAgreementService {
  private http = inject(HttpClient);
  private config = inject<AppConfig>(APP_CONFIG);
  private authService = inject(AuthService);

  private readonly API_URL = this.config.resourceApiURI + '/workerAgreements';

  constructor() { }

  cancel(agreementId: number): Observable<any> {
    const url = `${this.API_URL}/${agreementId}/cancel`;
    return this.http.post<any>(url, null);
  }

  deleteAgreement(agreementId: number): Observable<any> {
    const url = `${this.API_URL}/${agreementId}`;
    return this.http.delete<any>(url);
  }

  createWorkerAgreement(workerAgreement: WorkerAgreement): Observable<number> {
    const url = `${this.API_URL}/workerAgreement`;
    return this.http.post<number>(url, workerAgreement);
  }

  createMultipleWorkerAgreements = (request: CreateMultipleWorkerAgreementsRequest) => this.http.post(`${this.API_URL}/multiple`, request);

  sendMultipleWorkerAgreementsToApproval = (request: SendMultipleWorkerAgreementsToApprovalRequest) =>
    this.http.post(`${this.API_URL}/multiple/sendToApproval`, request);

  approveAgreementVoidingWithToken(agreementId: number, request: ApproveAgreementVoidingWithTokenRequest): Observable<any> {
    const url = `${this.API_URL}/${agreementId}/approveVoidingWithToken`;
    return this.http.post<any>(url, request);
  }

  approveAgreementVoiding(agreementId: number): Observable<any> {
    const url = `${this.API_URL}/${agreementId}/approveVoiding`;
    return this.http.post<any>(url, null);
  }

  voidAgreement(agreementId: number, request: VoidAgreementRequest): Observable<any> {
    const url = `${this.API_URL}/${agreementId}/void`;
    return this.http.post<any>(url, request);
  }

  generateAgreementApprovalCode(request: GenerateAgreementApprovalCodeRequest): Observable<any> {
    const url = `${this.API_URL}/generateAgreementApprovalCode`;
    return this.http.post<any>(url, request);
  }

  getAllWorkersAgreements(
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
    filters: Filter[],
  ): Observable<IPagedResult<AllWorkerAgreementGridDto>> {
    const url = `${this.API_URL}/allWorkerAgreements`;

    let params = new HttpParams()
      .set('page', `${page || 1}`)
      .set('count', `${count || 10}`)
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection)
      .set('filterString', JSON.stringify(filters));

    const httpOptions = {
      params: params,
    };

    return this.http.get<any>(url, httpOptions);
  }

  addWorkerAgreement(workerAgreement: WorkerAgreement): Observable<any> {
    const url = `${this.API_URL}/workerAgreement`;
    return this.http.post<any>(url, workerAgreement);
  }

  getWorkerAgreement(agreementId: number): Observable<WorkerAgreement> {
    const url = `${this.API_URL}/workerAgreement/${agreementId}`;
    return this.http.get<WorkerAgreement>(url);
  }

  getPagedAgreementsByWorkerId(
    workerId: number,
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
  ): Observable<IPagedResult<WorkerAgreementGridDto>> {
    const url = `${this.API_URL}/workers/${workerId}/paged`;

    let params = new HttpParams()
      .set('page', `${page || 1}`)
      .set('count', `${count || 10}`)
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection);

    const httpOptions = {
      params: params,
    };

    return this.http.get<IPagedResult<WorkerAgreementGridDto>>(url, httpOptions);
  }

  getAgreementsByWorkerId(workerId: number): Observable<AgreementToPick[]> {
    const url = `${this.API_URL}/workers/${workerId}`;
    return this.authService.isAuthenticated$.pipe(
      first(isAuthenticated => isAuthenticated),
      switchMap(() => this.http.get<AgreementToPick[]>(url))
    );
  }

  hasWorkerActiveAgreement(workerId: number): Observable<ActiveAgreementDto> {
    const url = `${this.API_URL}/hasWorkerActiveAgreement/${workerId}`;
    return this.http.get<ActiveAgreementDto>(url);
  }

  GetWorkerActiveAgreementEmploymentTypes(workerId: number): Observable<EmploymentType[]> {
    const url = `${this.API_URL}/getWorkerActiveAgreementEmploymentTypes/${workerId}`;
    return this.http.get<EmploymentType[]>(url);
  }

  GetWorkerAgreementEmploymentTypes(workerId: number): Observable<EmploymentType[]> {
    const url = `${this.API_URL}/getWorkerAgreementEmploymentTypes/${workerId}`;
    return this.http.get<EmploymentType[]>(url);
  }

  getOverlappingWorkerAgreements(
    workerId: number,
    workerAgreementId: number,
    employerObjectId: number,
    employmentDateFrom: Moment,
    employmentDateTo: Moment,
  ) {
    const url = `${this.API_URL}/overlapping`;

    let params = new HttpParams()
      .set('workerId', `${workerId}`)
      .set('employerObjectId', `${employerObjectId}`)
      .set('employmentDateFrom', `${employmentDateFrom.format('YYYY-MM-DD')}`);

    if (!!workerAgreementId) {
      params = params.set('workerAgreementId', `${workerAgreementId}`);
    }

    if (!!employmentDateTo) {
      params = params.set('employmentDateTo', `${employmentDateTo.format('YYYY-MM-DD')}`);
    }

    return this.http.get<number[]>(url, {
      params: params,
    });
  }

  getWorkerAgreementDetails(agreementId: number): Observable<WorkerAgreementDetailsDto> {
    const url = `${this.API_URL}/workerAgreementDetails/${agreementId}`;
    return this.http.get<WorkerAgreementDetailsDto>(url);
  }

  extendWorkerMandateAgreement(agreementId: number): Observable<number> {
    const url = `${this.API_URL}/${agreementId}/extendMandateAgreement`;
    return this.http.post<number>(url, null);
  }

  replaceWorkerAgreement(workerAgreementId: number): Observable<number> {
    const url = `${this.API_URL}/${workerAgreementId}/replace`;
    return this.http.post<number>(url, null);
  }

  sendAgreementExtensionConsent(requests: SendAgreementExtensionConsentRequest[]) {
    const url = `${this.API_URL}/sendAgreementExtensionConsent`;
    return this.http.post<any>(url, requests);
  }

  sendWorkerAgreementToApproval(agreementId: number): Observable<any> {
    const url = `${this.API_URL}/${agreementId}/sendToApproval`;
    return this.http.post<any>(url, null);
  }

  sendWorkerAgreementToVerification(agreementId: number): Observable<any> {
    const url = `${this.API_URL}/${agreementId}/sendToVerification`;
    return this.http.post<any>(url, null);
  }

  sign(agreementId: number, request: SignWorkerAgreementRequest): Observable<any> {
    const url = `${this.API_URL}/${agreementId}/sign`;
    return this.http.post<any>(url, request);
  }

  terminate(workerAgreementId: number, request: TerminateAgreementRequest): Observable<any> {
    const url = `${this.API_URL}/${workerAgreementId}/terminate`;
    return this.http.post<any>(url, request);
  }

  undoVoidingAgreement(request: UndoVoidingAgreementRequest): Observable<any> {
    const url = `${this.API_URL}/undoVoidingAgreement`;
    return this.http.post<any>(url, request);
  }

  verifyAgreementApprovalCode(request: VerifyAgreementApprovalCodeRequest): Observable<AddEmployeeAgreementResponse> {
    const url = `${this.API_URL}/verifyAgreementApprovalCode`;
    return this.http.post<AddEmployeeAgreementResponse>(url, request);
  }

  setWaitingForGenerationStatus = (agreementId: number): Observable<any> =>
    this.http.post<any>(`${this.API_URL}/${agreementId}/setWaitingForGenerationStatus`, null);

  updateWorkerAgreement(agreementId: number, workerAgreement: WorkerAgreement): Observable<number> {
    const url = `${this.API_URL}/workerAgreement/${agreementId}`;
    return this.http.put<number>(url, workerAgreement);
  }

  getAbsenceTypesByWorkerAgreementId(workerAgreementId: number): Observable<DictionaryItem[]> {
    const url = `${this.API_URL}/${workerAgreementId}/absenceTypes`;
    return this.http.get<DictionaryItem[]>(url);
  }

  getHolidaysByWorkerAgreementId(workerAgreementId: number): Observable<HolidayCalendarDto> {
    const url = `${this.API_URL}/${workerAgreementId}/holidays`;
    return this.http.get<HolidayCalendarDto>(url);
  }

  getAbsenceSubtypesByWorkerAgreementId(absenceTypeId: number, workerAgreementId: number, absenceId: number | null): Observable<AbsenceSubtypeDto[]> {
    const url = `${this.API_URL}/${workerAgreementId}/absenceSubtypes`;
    return this.http.get<AbsenceSubtypeDto[]>(url, {
      params: new HttpParams({
        fromObject: {
          absenceTypeId: String(absenceTypeId),
          ...(!!absenceId && { absenceId: String(absenceId) }),
        },
      }),
    });
  }

  getExtensionMultipleMandateAgreementBaseData(agreementId: number): Observable<WorkerAgreement> {
    const url = `${this.API_URL}/getExtensionMultipleMandateAgreementBaseData/${agreementId}`;
    return this.http.get<WorkerAgreement>(url).pipe(
      map(
        (res) =>
          <WorkerAgreement>{
            ...res,
            ConclusionDate: res.ConclusionDate && new Date(res.ConclusionDate),
            EmploymentDateFrom: res.EmploymentDateFrom && new Date(res.EmploymentDateFrom),
            EmploymentDateTo: res.EmploymentDateTo && new Date(res.EmploymentDateTo),
          },
      ),
    );
  }

  getWorkerPIT2AgreementsCompanies(workerId: number): Observable<WorkerPIT2AgreementsCompaniesDto> {
    const url = `${this.API_URL}/verifyAgreementsCompaniesToFetch/${workerId}`;
    return this.http.get<WorkerPIT2AgreementsCompaniesDto>(url);
  }

  getNoticePeriod(
    workerId: number,
    employerId: number,
    agreementTypeId: AgreementTypeEnum,
    workerAgreementDateFrom: Date,
    workerAgreementDateTo: Date = null,
  ): Observable<NoticePeriodDto> {
    const url = `${this.API_URL}/worker/${workerId}/employer/${employerId}/agreementType/${agreementTypeId}/noticePeriod`;

    let params = new HttpParams();
    params = params.set('workerAgreementDateFrom', `${workerAgreementDateFrom.toDateString()}`);

    if (workerAgreementDateTo) {
      params = params.set('workerAgreementDateTo', `${workerAgreementDateTo.toDateString()}`);
    }

    return this.http.get<NoticePeriodDto>(url, { params });
  }

  getNoticePeriodStartDate(noticePeriodTypeId: number, terminationSubmissionDate: Date): Observable<Date> {
    const url = `${this.API_URL}/noticePeriod/${noticePeriodTypeId}/startDate`;

    let params = new HttpParams().set('terminationSubmissionDate', `${terminationSubmissionDate.toDateString()}`);

    return this.http.get<Date>(url, { params });
  }

  getNoticePeriodEndDate(noticePeriodTypeId: number, noticePeriodStartDate: Date): Observable<Date> {
    const url = `${this.API_URL}/noticePeriod/${noticePeriodTypeId}/endDate`;

    let params = new HttpParams().set('noticePeriodStartDate', `${noticePeriodStartDate.toDateString()}`);

    return this.http.get<Date>(url, { params });
  }

  getMultipleWorkerAgreementsActionConfig(filters: Filter[]): Observable<MultipleWorkerAgreementsActionConfigDto> {
    const url = `${this.API_URL}/multipleWorkerAgreementsActionConfig`;

    let params = new HttpParams()
      .set('filterString', JSON.stringify(filters));

    return this.http.get<MultipleWorkerAgreementsActionConfigDto>(url, { params })
  }

  getMultipleWorkerAgreementsActionSelectedWorkers(
    filters: Filter[],
    sortingField: string,
    sortingDirection: string = 'desc',): Observable<MultipleWorkerAgreementsActionSelectedWorkersDto[]> {
    const url = `${this.API_URL}/multipleWorkerAgreementsActionSelectedWorkers`;

    let params = new HttpParams()
      .set('filterString', JSON.stringify(filters))
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection);

    return this.http.get<MultipleWorkerAgreementsActionSelectedWorkersDto[]>(url, { params })
  }

  getMultipleWorkerAgreementsActionSelectedWorkerAgreementIds(filters: Filter[]): Observable<MultipleWorkerAgreementsActionSelectedWorkerAgreementDto[]> {
    const url = `${this.API_URL}/multipleWorkerAgreementsActionSelectedWorkerAgreementIds`;

    let params = new HttpParams()
      .set('filterString', JSON.stringify(filters));

    return this.http.get<MultipleWorkerAgreementsActionSelectedWorkerAgreementDto[]>(url, { params })
  }

  getAllMandateAgreementWorkedDayDocuments(
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string,
    filters: Filter[]) {
    const url = `${this.API_URL}/allMandateAgreementWorkedDayDocuments`;

    let params = new HttpParams()
      .set('page', `${page || 1}`)
      .set('count', `${count || 10}`)
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection)
      .set('filterString', JSON.stringify(filters));

    const httpOptions = {
      params: params,
    };

    return this.http.get<IPagedResult<MandateAgreementWorkedDayGridDto>>(url, httpOptions);
  }

  getPagedMandateAgreementWorkedDaysByWorkerId(
    workerId: number,
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
  ): Observable<IPagedResult<MandateAgreementWorkedDaysWorkerGridDto>> {
    const url = `${this.API_URL}/workers/${workerId}/mandateAgreementWorkedDays/paged`;

    let params = new HttpParams()
      .set('page', `${page || 1}`)
      .set('count', `${count || 10}`)
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection);

    const httpOptions = {
      params: params,
    };

    return this.http.get<IPagedResult<MandateAgreementWorkedDaysWorkerGridDto>>(url, httpOptions)
  };

  getActiveWorkerAgreementsByQrCode(workerId: number, qrCode: string): Observable<AgreementToLogWork[]> {
    const url = `${this.API_URL}/workers/${workerId}/qrCode/${qrCode}`;
    const context = new HttpContext().set(BYPASS_ERROR_HANDLER, true);
    return this.http.get<AgreementToLogWork[]>(url, { context });
  }

  getLegalizationDataForWorkerAgreements(agreementIds: number[]): Observable<LegalizationDataForWorkerAgreementDto[]> {
    const url = `${this.API_URL}/getLegalizationDataForWorkerAgreements`;

    let params = new HttpParams();
    params = params.appendAll({ agreementIds: agreementIds });

    return this.http.get<LegalizationDataForWorkerAgreementDto[]>(url, { params });
  }

  getWorkerAgreementForAnnex(workerAgreementId: number): Observable<WorkerAgreementForAnnexDto> {
    const url = `${this.API_URL}/${workerAgreementId}/getDataForAnnex`;
    return this.http.get<WorkerAgreementForAnnexDto>(url);
  }

  hasAccessToSupervisorJobTitle(): Observable<boolean> {
    const url = `${this.API_URL}/hasAccessToSupervisorJobTitle`;
    return this.authService.isAuthenticated$.pipe(
      first(isAuthenticated => isAuthenticated),
      switchMap(() => this.http.get<boolean>(url))
    );
  }

  getWorkerAgreementPanelDetails(workerAgreementId: number, showAll: boolean = false): Observable<WorkerAgreementDetailsResponse> {
    const url = `${this.API_URL}/${workerAgreementId}/details`;
    const params = showAll ? { params: { showAll: 'true' } } : {};
    return this.http.get<WorkerAgreementDetailsResponse>(url, params);
  }
}
